import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #isRemovingGroupedItems = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    const { quantity, cartLine: line, lineItemKey } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      await this.onLineItemRemove(line);
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      lineItemKey,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const validation = await this.#getCpqRemovalValidation(line);
    if (!validation.canRemove) {
      this.#openCpqMinRemovalModal(validation);
      return;
    }

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const lineItemKey = cartItemRowToRemove.dataset.key;

    this.updateQuantity({
      line,
      quantity: 0,
      lineItemKey,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Validates whether removing a line would break CPQ min quantity rules for grouped lines.
   * Grouping key: WEBCONFIGID + WEBHEADERID.
   * @param {number} line - 1-based cart line index.
   * @returns {Promise<{
   *   canRemove: boolean,
   *   minQuantity: number | null,
   *   groupedItems: Array<{ key: string, quantity: number, properties?: Record<string, string> }>
   * }>} Validation result and grouped line details.
   */
  async #getCpqRemovalValidation(line) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const lineIndex = line - 1;

      if (!cart.items || !cart.items[lineIndex]) {
        return { canRemove: true, minQuantity: null, groupedItems: [] };
      }

      /** @type {Array<{ key: string, quantity: number, properties?: Record<string, string> }>} */
      const cartItems = cart.items;
      const cartItem = cart.items[lineIndex];
      const minQty = cartItem.properties?._CUSTMOQ;

      if (minQty === undefined || minQty === null) {
        return { canRemove: true, minQuantity: null, groupedItems: [] };
      }

      const minQuantity = parseInt(minQty, 10);
      if (Number.isNaN(minQuantity)) {
        return { canRemove: true, minQuantity: null, groupedItems: [] };
      }

      const webConfigId = cartItem.properties?.WEBCONFIGID;
      const webHeaderId = cartItem.properties?.WEBHEADERID;
      const hasGroupingKeys = webConfigId !== undefined && webHeaderId !== undefined;

      const groupedItems = hasGroupingKeys
        ? cartItems.filter((item) => {
          return item.properties?.WEBCONFIGID === webConfigId
            && item.properties?.WEBHEADERID === webHeaderId;
        })
        : [cartItem];

      const groupedCurrentQuantity = groupedItems.reduce((total, item) => total + item.quantity, 0);
      const quantityForValidation = groupedCurrentQuantity - (cartItem.quantity || 0);

      if (typeof CpqCustomizer !== 'undefined' && CpqCustomizer.hasMinQty) {
        return {
          canRemove: CpqCustomizer.hasMinQty(minQuantity, quantityForValidation),
          minQuantity,
          groupedItems,
        };
      }

      return {
        canRemove: quantityForValidation >= minQuantity,
        minQuantity,
        groupedItems,
      };
    } catch (error) {
      console.error('Error validating grouped minimum quantity before removal:', error);
      return { canRemove: true, minQuantity: null, groupedItems: [] };
    }
  }

  /**
   * Opens CPQ min quantity removal modal with "Remove all" and "Go back" actions.
   * @param {{ minQuantity: number | null, groupedItems: Array<{ key: string, quantity: number, properties?: Record<string, string> }> }} validation
   */
  #openCpqMinRemovalModal(validation) {
    const genericModal = /** @type {{ show: (title?: string, body?: string, options?: object) => void } | undefined} */ (
      /** @type {any} */ (window).genericModal
    );

    if (genericModal) {
      const minQty = validation.minQuantity ?? 0;
      genericModal.show(
        'Minimum quantity required',
        `There is a minimum quantity of ${minQty}. Remove all variants or go back?`,
        {
          primaryButtonText: 'Remove All',
          secondaryButtonText: 'Go Back',
          onPrimaryClick: () => {
            this.#removeGroupedLineItems(validation.groupedItems);
          },
        }
      );
    }
  }

  /**
   * Removes all grouped line items from cart in a single request.
   * @param {Array<{ key: string, quantity: number, properties?: Record<string, string> }>} groupedItems
   */
  #removeGroupedLineItems(groupedItems) {
    if (!groupedItems.length || this.#isRemovingGroupedItems) return;

    this.#isRemovingGroupedItems = true;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear-group:user-action');
    this.#disableCartItems();

    const { cartTotal } = this.refs;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    /** @type {Record<string, number>} */
    const updates = {};
    groupedItems.forEach((item) => {
      updates[item.key] = 0;
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: this.#getSectionsUrl(),
    });

    cartTotal?.shimmer();

    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#isRemovingGroupedItems = false;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.lineItemKey] - The cart line item key.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, lineItemKey } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: lineItemKey,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: this.#getSectionsUrl(),
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets a section rendering URL that can return theme sections.
   *
   * App proxy pages under /a/package-builder can successfully mutate the cart,
   * but they do not reliably return drawer section HTML for bundled cart updates.
   */
  #getSectionsUrl() {
    if (window.location.pathname.includes('/a/package-builder')) {
      return Theme.routes.root_url || '/';
    }

    return window.location.pathname;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
